/**
 * Presentation logic for the policy decision ledger (migration 127/128).
 *
 * WHY A SEPARATE MODULE. One table holds five different subject kinds and eight interactions, so
 * naming a column after a field produces a cell that means something different on every row -
 * `subject_label` is "prod @ serial 42" here and "acme/vpc 1.2.0" on the line below. The columns
 * are therefore named after QUESTIONS (when / what happened / to what / under which rule / why),
 * and this module is the single place each kind answers them. Rejected alternative: doing it inline
 * in the panel, which is exactly what the old flat-span row did and why two rows could not be
 * compared.
 *
 * `href` is a string rather than a rendered <Link>, so this stays plain TypeScript and the whole
 * mapping is unit-testable without rendering - the same split as lib/findingView.ts.
 */
import type { PolicyArtefactType, PolicyCoverageItem, PolicyDecision, PolicySettings } from '@/lib/apiTypes'

// ── Filter vocabularies ──────────────────────────────────────────────────────

export type VerdictFilter = 'all' | 'block' | 'override' | 'warn'
export type InteractionFilter = 'all' | 'state' | 'plan' | 'module' | 'provider'

export const VERDICT_FILTERS: VerdictFilter[] = ['all', 'block', 'override', 'warn']
export const VERDICT_FILTER_LABEL: Record<VerdictFilter, string> = {
  all: 'All',
  block: 'Refusals',
  override: 'Overrides',
  warn: 'Warnings',
}
/** `all` is deliberately absent so it keeps the neutral accent (the SegmentedFilter convention). */
export const VERDICT_FILTER_COLOR: Partial<Record<VerdictFilter, string>> = {
  block: 'var(--red)',
  override: 'var(--amber)',
}

/**
 * Interaction buckets, matched by PREFIX.
 *
 * The previous version matched exact interaction values and covered only four of the eight, so
 * `state.push`, `state.attest`, `state.serve_override` and `plan.approve` belonged to no segment at
 * all. Because overrides are mostly `state.attest` and `plan.approve`, picking any interaction
 * segment made the entire override half of the ledger vanish. A prefix bucket is a PARTITION, which
 * is also what makes `all` equal the sum of the others.
 */
export const INTERACTION_FILTERS: InteractionFilter[] = ['all', 'state', 'plan', 'module', 'provider']
export const INTERACTION_FILTER_LABEL: Record<InteractionFilter, string> = {
  all: 'All',
  state: 'State',
  plan: 'Plans',
  module: 'Modules',
  provider: 'Providers',
}

export type InteractionBucket = Exclude<InteractionFilter, 'all'>

/** The segment an interaction belongs to. Unknown values bucket by their own prefix, so a future
 *  interaction stays reachable by filter instead of silently dropping out of every bucket. */
export function interactionBucket(interaction: string): InteractionBucket {
  const prefix = interaction.split('.')[0]
  return prefix === 'plan' || prefix === 'module' || prefix === 'provider' ? prefix : 'state'
}

// ── Labels ───────────────────────────────────────────────────────────────────

/**
 * Verdict-NEUTRAL names for what terraform was doing.
 *
 * DecisionTimeline's own map says "apply refused" / "module refused", which is right there - the
 * label is the whole sentence on a rail with no columns. In a table with its own Verdict column it
 * says the same thing twice, and on an override row it would fight the badge. Here the interaction
 * is the noun and the verdict is the badge.
 */
const INTERACTION_LABEL: Record<string, string> = {
  'state.lock': 'apply lock',
  'state.push': 'state push',
  'state.attest': 'attestation',
  'state.serve_override': 'enforcement change',
  'plan.push': 'plan push',
  'plan.approve': 'plan approval',
  'module.download': 'module download',
  'provider.download': 'provider download',
  'state.pull': 'state read',
}

const SOURCE_LABEL: Record<string, string> = {
  builtin: 'built-in gate',
  opa: 'custom Rego',
  scan: 'scanner',
  manual: 'admin action',
  serve_override: 'serve override',
  block_all: 'consumption blocked',
}

const SUBJECT_KIND_LABEL: Record<string, string> = {
  state_workspace: 'workspace',
  state_version: 'state',
  plan_version: 'plan',
  module_version: 'module',
  provider_version: 'provider',
}

/** Every label lookup falls back to the RAW value: an unmapped enum shows its real name rather
 *  than being relabelled or vanishing. Same rule as findingView's category handling. */
export const interactionLabel = (i: string): string => INTERACTION_LABEL[i] ?? i
export const sourceLabel = (s: string): string => SOURCE_LABEL[s] ?? s
export const subjectKindLabel = (k: string): string => SUBJECT_KIND_LABEL[k] ?? k

export function verdictBadge(verdict: string): { cls: string; text: string } {
  if (verdict === 'block') return { cls: 'badge-red', text: 'BLOCKED' }
  if (verdict === 'override') return { cls: 'badge-amber', text: 'OVERRIDE' }
  return { cls: 'badge-neutral', text: verdict === 'warn' ? 'WARN' : verdict.toUpperCase() }
}

/**
 * `terraform 1.9.5`, `opentofu 1.8.2`, `client 1.2.3`, or null.
 *
 * Null is the common case on the state-backend path and is not a gap in the data. Measured against
 * OpenTofu v1.13.1 and Terraform v1.16.2, every state request - GET, LOCK, POST and UNLOCK - goes
 * out as `User-Agent: Go-http-client/1.1`: no product, no version, byte-identical between the two.
 * The same tofu binary DOES identify itself on the registry paths, which is why module and
 * provider rows carry a tool and state rows do not. Only the version is recoverable here, out of
 * terraform's own LockInfo body.
 *
 * Not quite impossible, though: OpenTofu (not Terraform) honours a `headers` map in the backend
 * block, including `User-Agent`, so an org that sets one gets attributed normally through this
 * same path. Hence "not sent" rather than "cannot be known".
 */
export function toolLabel(tool: string | null, version: string | null): string | null {
  if (!tool || tool === 'other') return version ? `client ${version}` : null
  return version ? `${tool} ${version}` : tool
}

/** Which knob to go and turn, when there is no policy row to name. */
export function policyAuthority(d: PolicyDecision): string {
  if (d.policy_name) return d.policy_name
  if (d.source === 'builtin') return 'built-in gate'
  if (d.source === 'manual' || d.source === 'serve_override') return 'admin action'
  return sourceLabel(d.source)
}

// ── The subject cell ─────────────────────────────────────────────────────────

export interface SubjectRef {
  kindLabel: string
  /** Always rendered, link or not. */
  label: string
  /** The raw coordinate, surfaced in the expansion so a support conversation can start. */
  key: string | null
  /** In-app route, or null when it could not be built. */
  href: string | null
  /** A full sentence whenever href is null; never an empty string. */
  unlinkableReason: string | null
}

const NO_KEY = 'Recorded before this row carried machine coordinates, so there is nothing to link to.'
const BAD_KEY = 'The recorded coordinates do not match any artefact page.'

/**
 * Build the subject cell, including its route.
 *
 * Deliberately conservative: anything it cannot parse with full confidence yields `href: null` and
 * a sentence, because a link that goes to the WRONG artefact is worse than no link. Every segment
 * is percent-encoded, matching how Providers.tsx builds its own routes.
 */
export function subjectRef(d: PolicyDecision, orgSlug: string): SubjectRef {
  const base = {
    kindLabel: subjectKindLabel(d.subject_kind),
    label: d.subject_label,
    key: d.subject_key,
  }
  if (!d.subject_key) return { ...base, href: null, unlinkableReason: NO_KEY }

  const org = encodeURIComponent(orgSlug)
  const e = encodeURIComponent
  const sep = d.subject_key.indexOf(':')
  const prefix = sep === -1 ? '' : d.subject_key.slice(0, sep)
  const rest = sep === -1 ? '' : d.subject_key.slice(sep + 1)
  const bad = { ...base, href: null, unlinkableReason: BAD_KEY }
  if (!rest) return bad

  // `name@version` tail, used by both artefact forms.
  const atSplit = (s: string): [string, string] | null => {
    const at = s.lastIndexOf('@')
    return at <= 0 || at === s.length - 1 ? null : [s.slice(0, at), s.slice(at + 1)]
  }

  switch (prefix) {
    case 'workspace':
      return { ...base, href: `/org/${org}/state/${e(rest)}`, unlinkableReason: null }
    case 'state':
    case 'plan': {
      const slash = rest.indexOf('/')
      if (slash <= 0 || slash === rest.length - 1) return bad
      const ws = rest.slice(0, slash)
      const id = rest.slice(slash + 1)
      const seg = prefix === 'state' ? 'state' : 'plans'
      return { ...base, href: `/org/${org}/${seg}/${e(ws)}/${e(id)}`, unlinkableReason: null }
    }
    case 'module': {
      const parsed = atSplit(rest)
      if (!parsed) return bad
      const [coords, version] = parsed
      // namespace/name/provider - the provider segment is the whole reason subject_key exists,
      // since subject_label never carried it.
      const parts = coords.split('/')
      if (parts.length !== 3 || parts.some((p) => p === '')) return bad
      const [, name, provider] = parts as [string, string, string]
      return {
        ...base,
        href: `/org/${org}/modules/${e(name)}/${e(provider)}/${e(version)}`,
        unlinkableReason: null,
      }
    }
    case 'provider': {
      const parsed = atSplit(rest)
      if (!parsed) return bad
      const [coords, version] = parsed
      const parts = coords.split('/')
      if (parts.length !== 3 || parts.some((p) => p === '')) return bad
      const [upstream, namespace, type] = parts as [string, string, string]
      // ?v= is the documented version seed on the providers page.
      return {
        ...base,
        href: `/org/${org}/providers/${e(upstream)}/${e(namespace)}/${e(type)}?v=${e(version)}`,
        unlinkableReason: null,
      }
    }
    default:
      return bad
  }
}

// ── One row ──────────────────────────────────────────────────────────────────

export interface DecisionView {
  id: string
  at: number
  verdict: string
  badge: { cls: string; text: string }
  interaction: string
  interactionLabel: string
  bucket: InteractionBucket
  subject: SubjectRef
  policyId: string | null
  policyLabel: string
  sourceLabel: string
  reason: string | null
  actor: string | null
  actorType: string | null
  client: string | null
  reqId: string | null
  traceId: string | null
  /** The untouched row, for the expansion. */
  raw: PolicyDecision
}

export function decisionViewOf(d: PolicyDecision, orgSlug: string): DecisionView {
  return {
    id: d.id,
    at: d.decided_at,
    verdict: d.verdict,
    badge: verdictBadge(d.verdict),
    interaction: d.interaction,
    interactionLabel: interactionLabel(d.interaction),
    bucket: interactionBucket(d.interaction),
    subject: subjectRef(d, orgSlug),
    policyId: d.policy_id,
    policyLabel: policyAuthority(d),
    sourceLabel: sourceLabel(d.source),
    reason: d.reason,
    actor: d.actor_name,
    actorType: d.actor_type,
    client: toolLabel(d.tool, d.tool_version),
    reqId: d.req_id,
    traceId: d.trace_id,
    raw: d,
  }
}

/** Order is preserved: the server already sorted newest-first and the view must not re-sort. */
export const decisionViews = (rows: PolicyDecision[], orgSlug: string): DecisionView[] =>
  rows.map((d) => decisionViewOf(d, orgSlug))

// ── The two compact cards, and the strip ─────────────────────────────────────

export interface RankedPolicy {
  policyId: string
  name: string
  artefactType: PolicyArtefactType
  blocks: number
  /** Share of the top row's blocks, so the bar is comparative rather than absolute. */
  pct: number
  /** Enabled but never evaluated against anything - the common case for state/provider/plan. */
  neverEvaluated: boolean
  statusBadge: 'disabled' | 'advisory' | null
  lastBlockAt: number | null
}

export function topPoliciesByRefusal(cov: PolicyCoverageItem[], limit = 6): RankedPolicy[] {
  const firing = cov.filter((c) => c.block_count > 0)
  const top = [...firing].sort((a, b) => b.block_count - a.block_count || a.policy_name.localeCompare(b.policy_name))
  const peak = top[0]?.block_count ?? 1
  return top.slice(0, limit).map((c) => ({
    policyId: c.policy_id,
    name: c.policy_name,
    artefactType: c.artefact_type,
    blocks: c.block_count,
    pct: (c.block_count / peak) * 100,
    neverEvaluated: c.pass_count + c.fail_count === 0,
    statusBadge: c.is_enabled !== 1 ? 'disabled' : c.is_enforcing !== 1 ? 'advisory' : null,
    lastBlockAt: c.last_block_at,
  }))
}

/** "3 of 14" - how many authored policies have actually refused something. */
export function policiesFired(cov: PolicyCoverageItem[]): { fired: number; total: number } {
  return { fired: cov.filter((c) => c.block_count > 0).length, total: cov.length }
}

/** Enabled policies that have never been evaluated against any artefact. The genuinely useful
 *  diagnostic: a rule that cannot fire looks identical to one that simply has not. */
export function neverEvaluatedCount(cov: PolicyCoverageItem[]): number {
  return cov.filter((c) => c.is_enabled === 1 && c.pass_count + c.fail_count === 0).length
}

export function policyArtefactIndex(cov: PolicyCoverageItem[]): Record<string, PolicyArtefactType> {
  const out: Record<string, PolicyArtefactType> = {}
  for (const c of cov) out[c.policy_id] = c.artefact_type
  return out
}

/**
 * Artefacts a policy would refuse RIGHT NOW.
 *
 * An inventory, not an event count - which is why the tile beside the two 30-day counts has to say
 * so. Two of the three fields are optional in PolicySettings ("0 pre-migration"), so an absent one
 * is omitted from `parts` rather than printed as a zero that looks measured.
 */
export function blockedNow(s: PolicySettings | null): { total: number; parts: Array<{ label: string; n: number }> } {
  if (!s) return { total: 0, parts: [] }
  const parts: Array<{ label: string; n: number }> = [
    { label: 'module versions', n: s.modules_blocked_count },
  ]
  if (typeof s.state_blocked_count === 'number') parts.push({ label: 'workspaces', n: s.state_blocked_count })
  if (typeof s.providers_blocked_count === 'number') parts.push({ label: 'provider versions', n: s.providers_blocked_count })
  return { total: parts.reduce((n, p) => n + p.n, 0), parts }
}
